import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { MONTH_OPTIONS } from "@thkStore/Common/Constants";
import { useMergeStyles } from "@thkStore/Overrides/additionalStyles";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface ResourceItem {
    fields: {
        title: string;
        image: string;
        dateMonth: string;
        dateYear: number | null;
        description: string;
        buttonLink: LinkFieldValue;
        buttonLabel: string;
    };
}

const formatItemDate = (month: string, year: number | null): string => {
    if (!month && !year) {
        return "";
    }
    if (!month) {
        return `${year}`;
    }
    if (!year) {
        return month;
    }
    return `${month} ${year}`;
};

const enum fields {
    subheading = "subheading",
    heading = "heading",
    items = "items",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.subheading]: string;
        [fields.heading]: string;
        [fields.items]: ResourceItem[];
    };
    extendedStyles?: ResourcesAndInsightsStyles;
}

export interface ResourcesAndInsightsStyles {
    wrapper?: InjectableCss;
    innerContainer?: InjectableCss;
    header?: InjectableCss;
    subheadingText?: TypographyPresentationProps;
    headingText?: TypographyPresentationProps;
    grid?: InjectableCss;
    card?: InjectableCss;
    cardImage?: InjectableCss;
    cardBody?: InjectableCss;
    cardDate?: TypographyPresentationProps;
    cardDescription?: TypographyPresentationProps;
    cardButton?: ButtonPresentationProps;
}

export const resourcesAndInsightsStyles: ResourcesAndInsightsStyles = {
    wrapper: {
        css: css`
            background-color: ${getColor("secondary.main")};
            box-shadow: 0 0 0 100vmax ${getColor("secondary.main")};
            clip-path: inset(0 -100vmax);
        `,
    },
    innerContainer: {
        css: css`
            padding: 60px 0;
            display: flex;
            flex-direction: column;
            gap: 40px;

            @media (max-width: 768px) {
                padding: 40px 0;
                gap: 20px;
            }
        `,
    },
    header: {
        css: css`
            display: block;
            min-height: 45px;
        `,
    },
    subheadingText: {
        css: css`
            font-weight: 600;
            text-transform: uppercase;
            color: ${getColor("primary.main")};
            position: relative;
            padding-left: 12px;
            margin-bottom: 10px;

            &:before {
                content: "";
                position: absolute;
                background-color: ${getColor("primary.main")};
                width: 4px;
                height: 16px;
                top: 0;
                left: 0;
            }
        `,
    },
    headingText: {
        css: css`
            font-size: 36px;
            font-weight: 800;
            margin: 0;

            p {
                font-size: 36px;
                font-weight: 800;
                margin: 0;
                position: relative;
            }

            @media (max-width: 768px) {
                font-size: 22px;

                p {
                    font-size: 22px;
                }
            }
        `,
    },
    grid: {
        css: css`
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 17px;
            min-height: 305px;

            @media (max-width: 991px) {
                grid-template-columns: repeat(2, 1fr);
            }

            @media (max-width: 575px) {
                grid-template-columns: 1fr;
                gap: 21px;
            }
        `,
    },
    card: {
        css: css`
            display: flex;
            flex-direction: column;
            background-color: ${getColor("primary.contrast")};
            overflow: hidden;
            min-height: 305px;

            &:hover {
                box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.15);
            }
        `,
    },
    cardImage: {
        css: css`
            width: 100%;
            aspect-ratio: 10 / 4;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
        `,
    },
    cardBody: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 15px;
            flex: 1;
        `,
    },
    cardDate: {
        css: css`
            font-size: 12px;
            color: ${getColor("primary.main")};
            margin-bottom: 4px;
        `,
    },
    cardDescription: {
        css: css`
            font-size: 15px;
            font-weight: 600;
            margin-bottom: 8px;
            flex: 1;
        `,
    },
    cardButton: {
        css: css`
            width: fit-content;
        `,
    },
};

export const ResourcesAndInsights: FC<OwnProps> = ({ fields: widgetFields, extendedStyles }) => {
    const history = useHistory();
    const { isInShell } = useShellContext();
    const { links: buttonLinks } = useGetLinks(widgetFields.items || [], o => o.fields.buttonLink);
    const styles = useMergeStyles("resourcesAndInsights", resourcesAndInsightsStyles, extendedStyles);

    const onClick = (url?: string) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const items = widgetFields.items || [];

    return (
        <StyledWrapper className="resources-and-insights-wrapper" {...styles.wrapper}>
            <StyledWrapper className="ri-inner" {...styles.innerContainer}>
                <StyledWrapper className="ri-header" {...styles.header}>
                    {widgetFields.subheading && (
                        <Typography className="ri-subheading" {...styles.subheadingText}>
                            {widgetFields.subheading}
                        </Typography>
                    )}
                    {widgetFields.heading && (
                        <Typography as="h2" className="ri-heading" {...styles.headingText}>
                            {parse(widgetFields.heading, parserOptions)}
                        </Typography>
                    )}
                </StyledWrapper>

                {items.length > 0 && (
                    <StyledWrapper className="ri-grid" {...styles.grid}>
                        {items.map((item, index) => {
                            const link = buttonLinks[index];
                            const imgUrl = item.fields.image ? parseImageUrl(item.fields.image).url : "";

                            const cardImageStyles = {
                                css: css`
                                    ${styles.cardImage?.css || ""}
                                    ${imgUrl ? `background-image: url(${imgUrl});` : ""}
                                `,
                            };

                            return (
                                <StyledWrapper key={index} className="ri-card" {...styles.card}>
                                    {imgUrl && (
                                        <StyledWrapper className="ri-card-image" {...cardImageStyles} />
                                    )}
                                    <StyledWrapper className="ri-card-body" {...styles.cardBody}>
                                        {(() => {
                                            const formattedDate = formatItemDate(item.fields.dateMonth, item.fields.dateYear);
                                            return formattedDate ? (
                                                <Typography className="ri-card-date" {...styles.cardDate}>
                                                    {formattedDate}
                                                </Typography>
                                            ) : null;
                                        })()}
                                        {item.fields.description && (
                                            <Typography className="ri-card-description" {...styles.cardDescription}>
                                                {item.fields.description}
                                            </Typography>
                                        )}
                                        {link?.url && (
                                            <Button
                                                className="ri-card-button"
                                                variant="secondary"
                                                icon={{ src: ChevronRight, position: "right" }}
                                                {...styles.cardButton}
                                                onClick={() => onClick(link.url)}
                                            >
                                                {item.fields.buttonLabel || translate("Read more")}
                                            </Button>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: ResourcesAndInsights,
    definition: {
        group: "Basic",
        icon: "newspaper",
        displayName: "Resources & Insights",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.subheading,
                displayName: "Subheading",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "RichTextField",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.items,
                displayName: "Items",
                editorTemplate: "ListField",
                getDisplay: ({ fields: { title } }) => title || "Resource",
                defaultValue: [],
                tab: basicTab,
                fieldDefinitions: [
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "dateMonth",
                        displayName: "Date Month",
                        editorTemplate: "DropDownField",
                        options: MONTH_OPTIONS,
                        defaultValue: "",
                    },
                    {
                        name: "dateYear",
                        displayName: "Date Year",
                        editorTemplate: "IntegerField",
                        defaultValue: null,
                        min: 1900,
                    },
                    {
                        name: "description",
                        displayName: "Description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonLabel",
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "Read more",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
